import React from "react";
import { Table } from "react-bootstrap";
import useDownloadPDFAction from "@bloonde/billing/invoices/actions/download-pdf/DownloadPDFAction";

interface Props {
  data: {
    elements: any[];
    totalElements: number;
  };
  orderBy?: any;
  onOrderByChange?: any;
}

function BalanceSummaryTable({ data }: Props) {
  const { downloadPDFFunction } = useDownloadPDFAction();

  const grouped: Record<string, any[]> = data.elements.reduce((acc, el) => {
    const invoiceId = el.invoice_line?.invoice_id;
    const groupKey = invoiceId ? String(invoiceId) : "Sin factura";
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(el);
    return acc;
  }, {} as Record<string, any[]>);

  const orderedGroups = Object.entries(grouped).sort(([a], [b]) => {
    if (a === "Sin factura") return -1;
    if (b === "Sin factura") return 1;
    return Number(a) - Number(b);
  });

  const globalTotalPrice = data.elements.reduce(
    (acc, el) => acc + parseFloat(el.concept?.total_price || 0),
    0
  );
  const globalCommission = data.elements.reduce(
    (acc, el) => acc + parseFloat(el.concept?.commission_amount || 0),
    0
  );

  return (
    <>
      {/* Totales globales arriba a la derecha */}
      <div className="d-flex justify-content-end gap-5 fw-bold mb-3">
        <div>Total venta global: {formatCurrency(globalTotalPrice)}</div>
        <div>Comisión global: {formatCurrency(globalCommission)}</div>
      </div>

      {/* Tabla */}
      <Table bordered hover size="sm" responsive>
        <thead>
          <tr className="table-primary">
            <th>Productor / Pedido</th>
            <th>Factura</th>
            <th>Fecha</th>
            <th>Total</th>
            <th>Comisión</th>
          </tr>
        </thead>
        <tbody>
          {orderedGroups.map(([invoiceId, group]) => {
            const invoice = group[0]?.invoice_line?.invoice;
            const facturaReference = invoice?.reference ?? "";
            const pdfUrl = invoice?.pdf?.[0]?.url;

            const resumen = {
              productor: group[0]?.emitter?.user?.name ?? "Desconocido",
              factura: facturaReference,
              facturaUrl: pdfUrl,
              fecha: invoice?.date ?? "",
              total: group.reduce(
                (acc, el) => acc + parseFloat(el.concept?.total_price || 0),
                0
              ),
              comision: group.reduce(
                (acc, el) =>
                  acc + parseFloat(el.concept?.commission_amount || 0),
                0
              ),
            };

            return (
              <React.Fragment key={invoiceId}>
                <tr className="table-secondary fw-bold">
                  <td>{resumen.productor}</td>
                  <td>
                    {resumen.factura && resumen.facturaUrl ? (
                      <button
                        className="btn btn-link p-0 text-decoration-underline"
                        onClick={() => downloadPDFFunction(resumen.facturaUrl)}
                      >
                        {resumen.factura}
                      </button>
                    ) : (
                      resumen.factura
                    )}
                  </td>
                  <td>{formatDate(resumen.fecha)}</td>
                  <td>{formatCurrency(resumen.total)}</td>
                  <td>{formatCurrency(resumen.comision)}</td>
                </tr>
                {group.map((el) => (
                  <tr key={el.id}>
                    <td>
                      {el.concept?.order?.reference && el.concept?.order?.id ? (
                        <a
                          href={`/panel/1/1/producer/order/${el.concept.order.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {el.concept.order.reference}
                        </a>
                      ) : (
                        el.concept?.order?.reference ?? ""
                      )}
                    </td>
                    <td></td>
                    <td>{formatDate(el.created_at)}</td>
                    <td>{formatCurrency(el.concept?.total_price)}</td>
                    <td>{formatCurrency(el.concept?.commission_amount)}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

function formatCurrency(value: any): string {
  const num = typeof value === "string" ? parseFloat(value) : Number(value);
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(num || 0);
}

function formatDate(dateString: string): string {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch {
    return dateString;
  }
}

export default BalanceSummaryTable;
